<script>
import Layout from '@layouts/main'
import draggable from 'vuedraggable'
import { approverWorkflowMethods, roleMethods } from '@state/helpers'
import _ from 'lodash'
import * as Sentry from '@sentry/browser'

export default {
  metaInfo: {
    title: 'Edit Workflow',
    meta: [{ name: 'description', content: 'Edit Workflow' }],
  },
  components: { Layout, draggable },
  props: {
    workflow: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      selectedRole: null,
      roleItems: [],
      searchingRoles: false,
      roleSearch: '',
      roleSearchError: null,
      formError: null,
      isWorking: false,
      approverDialog: false,
      updateApproverOrderError: null,
      signoff: this.workflow.signoff,
    }
  },
  watch: {
    signoff: {
      handler() {
        this.tryUpdateApproverOrder()
      },
    },
    roleSearch: {
      handler() {
        this.searchRoles()
      },
    },
  },
  created() {
    this.tryUpdateApproverOrder = _.debounce(this.tryUpdateApproverOrder, 500)
  },
  methods: {
    ...approverWorkflowMethods,
    ...roleMethods,
    tryUpdateApproverOrder() {
      var order = {}
      this.updateApproverOrderError = null
      for (var i = 0; i < this.signoff.length; i++) {
        var item = this.signoff[i]
        item.order = i

        order[item.id] = i
      }

      return this.updateApproverOrder({
        workflowId: this.workflow.id,
        approvers: order,
      })
        .then(response => {})
        .catch(error => {
          this.updateApproverOrderError = error
        })
    },
    searchRoles() {
      this.roleSearchError = null
      this.getRoles({ search: this.roleSearch })
        .then(response => {
          this.roleItems = response.results
        })
        .catch(error => {
          this.roleSearchError = error
        })
    },
    save() {
      if (!this.selectedRole) {
        this.$swal('Error', 'You must choose a role.', 'error')
        return
      }

      var toSend = {
        workflowId: this.workflow.id,
        roleId: this.selectedRole.id,
      }
      this.addApproverToWorkflow({ signoff: toSend })
        .then(response => {
          this.signoff.push(response.data)
          this.close()
        })
        .catch(error => {
          this.$swal(
            'Error',
            typeof error === 'string' ||
              'An error occurred adding this approver to the workflow',
            'error'
          )

          Sentry.captureException(error)
        })
    },
    close() {
      this.selectedRole = null
      this.approverDialog = false
    },
    confirmSignoffDelete(approver) {
      this.$swal({
        title: 'Confirm',
        text: 'Are you sure you want to delete this approver?',
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete it!',
      }).then(result => {
        if (result.value) {
          this.tryDeleteApprover(approver)
        }
      })
    },
    tryDeleteApprover(approver) {
      this.deleteSignoff({ signoffId: approver.id })
        .then(response => {
          var index = this.signoff.indexOf(approver)
          if (index !== -1) {
            this.signoff.splice(index, 1)
          }
        })
        .catch(error => {
          this.$swal('Error', error.response.data, 'error')
        })
    },
  },
}
</script>

<template>
  <Layout>
    <v-toolbar flat color="white">
      <v-toolbar-title
        >"{{ workflow.role.name }}" Approval Workflow</v-toolbar-title
      >
      <v-spacer></v-spacer>
      <v-dialog v-model="approverDialog" max-width="500px">
        <v-btn slot="activator" color="cyan" dark depressed class="mb-2"
          >Add Approver</v-btn
        >
        <v-card class="px-3 py-2">
          <v-card-title>
            <span class="headline">Add Approver</span>
          </v-card-title>

          <v-card-text>
            <v-container grid-list-md class="pa-0">
              <v-layout wrap>
                <v-flex xs12>
                  <v-combobox
                    v-model="selectedRole"
                    :items="roleItems"
                    :loading="searchingRoles"
                    :search-input.sync="roleSearch"
                    item-text="name"
                    item-value="id"
                    label="Approver Role"
                    placeholder="Start typing to Search"
                    hint="Choose a role"
                    color="cyan"
                  ></v-combobox>
                </v-flex>
                <v-alert :value="formError" type="error">{{
                  formError
                }}</v-alert>
              </v-layout>
            </v-container>
          </v-card-text>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="cyan" dark depressed outline @click="close"
              >Cancel</v-btn
            >
            <v-btn color="cyan" dark depressed @click="save">
              <BaseIcon v-if="isWorking" name="sync" spin />
              <span v-else>Save</span>
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-toolbar>
    <table class="table table-striped">
      <thead>
        <tr>
          <th></th>
          <th>Signoff Role</th>
          <th>Actions</th>
        </tr>
      </thead>
      <draggable
        v-model="signoff"
        element="tbody"
        @start="drag = true"
        @end="drag = false"
      >
        <tr v-for="item in signoff" :key="item.id">
          <td colspan="1" :class="$style.drag"><v-icon>mdi-drag</v-icon></td>
          <td>{{ item.role.name }}</td>
          <td>
            <v-icon small class="mr-2" @click="confirmSignoffDelete(item)"
              >delete</v-icon
            >
          </td>
        </tr>
      </draggable>
    </table>
  </Layout>
</template>

<style lang="scss" module>
@import '@design';
</style>
